<template>
  <div class="hik-multi-wrap">
    <div v-if="tipText" class="hik-tip">{{ tipText }}</div>
    <div :id="containerId" class="hik-plugin-host" />
  </div>
</template>

<script>
/**
 * 海康 Web 插件多画面预览（依赖 public/index.html 已引入的 WebVideoCtrl + jsVideoPlugin，与 util/HKVideoPlugin3.9.9 一致）
 * 传入 cameras：每项含 ip、httpPort、username、password、iChannelID、streamType、iWndIndex（窗口号 0 起）
 */
export default {
  name: 'HikMultiPlayer',
  props: {
    /** 与 index.html 中插入的 DOM 对应，单页仅允许一个实例时使用唯一 id */
    containerId: {
      type: String,
      default: 'hik-cam-preview-root',
    },
    /** 1 | 2 | 3 → 插件分屏 1×1 / 2×2 / 3×3 */
    wallCols: {
      type: Number,
      default: 2,
    },
    /** 是否暂停取流（如切换 Tab 时停流，不销毁插件） */
    paused: {
      type: Boolean,
      default: false,
    },
    cameras: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      tipText: '',
      pluginReady: false,
      loggedIdentifies: [],
    };
  },
  computed: {
    wndType() {
      const c = this.wallCols;
      if (c >= 3) return 3;
      if (c === 2) return 2;
      return 1;
    },
  },
  watch: {
    paused(p) {
      if (p) this.stopStreamsOnly();
      else if (this.pluginReady) this.applyStreamsDebounced();
    },
    cameras: {
      deep: true,
      handler() {
        this.applyStreamsDebounced();
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initPlugin();
    });
  },
  beforeDestroy() {
    this.teardownAll();
  },
  methods: {
    applyStreamsDebounced() {
      if (this._deb) clearTimeout(this._deb);
      this._deb = setTimeout(() => {
        this._deb = null;
        if (!this.pluginReady || this.paused) return;
        this.applyStreams();
      }, 400);
    },

    getWv() {
      return typeof window !== 'undefined' ? window.WebVideoCtrl : null;
    },

    async initPlugin() {
      const WebVideoCtrl = this.getWv();
      if (!WebVideoCtrl) {
        this.tipText = '未找到全局 WebVideoCtrl，请确认 public/index.html 已引入 util/HKVideoPlugin3.9.9/webVideoCtrl.js';
        return;
      }
      try {
        const chk = WebVideoCtrl.I_CheckPluginInstall();
        if (chk === -1) {
          this.tipText = '未检测到海康 Web 插件，请先安装 HCWebSDKPlugin / WebComponentsKit（海康开发包内）。';
          return;
        }
      } catch (e) {
        this.tipText = '检测海康插件时异常：' + (e && e.message);
        return;
      }

      const vm = this;
      const domId = vm.containerId;
      /** 仅当 I_InsertOBJECTPlugin 成功（本地 ws 34686–34690 已连上）才允许 I_Login，否则会报 JS_StartSyncHttpTransmission is not a function */
      let insertOk = false;
      await new Promise((resolve) => {
        try {
          WebVideoCtrl.I_InitPlugin({
            iWndowType: vm.wndType,
            bWndFull: true,
            bDebugMode: false,
            cbSelWnd: function () {},
            cbDoubleClickWnd: function () {},
            cbEvent: function (iEventType, iParam1, iParam2) {
              console.log('Hik cbEvent', iEventType, iParam1, iParam2);
            },
            cbInitPluginComplete: function () {
              WebVideoCtrl.I_InsertOBJECTPlugin(domId).then(
                () => {
                  insertOk = true;
                  try {
                    if (typeof WebVideoCtrl.I_CheckPluginVersion === 'function') {
                      WebVideoCtrl.I_CheckPluginVersion().then(
                        (needUpgrade) => {
                          if (needUpgrade) {
                            vm.tipText = '检测到海康插件有新版本，请按海康说明升级 HCWebSDKPlugin。';
                          }
                        },
                        () => {}
                      );
                    }
                  } catch (e) {
                    /* ignore */
                  }
                  resolve();
                },
                () => {
                  vm.tipText =
                    '海康本地服务未连通（控制台若出现 ws://127.0.0.1:34686–34690 失败属此原因）。请在本机安装与当前 webVideoCtrl 同版本的 HCWebSDKPlugin/WebComponentsKit，安装后重启浏览器；确认任务栏或进程中有海康 WebSDK 本地服务，并放行防火墙。插件未就绪时不会尝试登录，可避免 JS_StartSyncHttpTransmission 报错。';
                  resolve();
                }
              );
            },
          });
        } catch (e) {
          vm.tipText = 'I_InitPlugin 失败：' + (e && e.message);
          resolve();
        }
      });

      if (!insertOk) {
        this.pluginReady = false;
        return;
      }
      this.pluginReady = true;
      this.tipText = '';
      if (!this.paused) this.applyStreams();
    },

    stopStreamsOnly() {
      const WebVideoCtrl = this.getWv();
      if (!WebVideoCtrl || !this.pluginReady) return;
      try {
        WebVideoCtrl.I_StopAllPlay().then(
          () => {},
          () => {}
        );
      } catch (e) {
        /* ignore */
      }
      (this.loggedIdentifies || []).forEach((sz) => {
        try {
          WebVideoCtrl.I_Logout(sz).then(
            () => {},
            () => {}
          );
        } catch (e) {
          /* ignore */
        }
      });
      this.loggedIdentifies = [];
    },

    applyStreams() {
      const WebVideoCtrl = this.getWv();
      if (!WebVideoCtrl || !this.pluginReady || this.paused) return;
      const list = (this.cameras || []).filter(Boolean);
      this.stopStreamsOnly();
      if (!list.length) return;

      const run = async () => {
        for (let i = 0; i < list.length; i++) {
          const c = list[i];
          const httpPort = c.httpPort != null ? Number(c.httpPort) : 80;
          const szDeviceIdentify = `${c.ip}_${httpPort}`;
          await new Promise((resolve) => {
            const onStart = () => {
              WebVideoCtrl.I_StartRealPlay(szDeviceIdentify, {
                iWndIndex: c.iWndIndex != null ? Number(c.iWndIndex) : i,
                iStreamType: c.streamType != null ? Number(c.streamType) : 1,
                iChannelID: c.iChannelID != null ? Number(c.iChannelID) : 1,
                success: () => resolve(),
                error: () => resolve(),
              });
            };
            WebVideoCtrl.I_Login(c.ip, 1, httpPort, c.username, c.password, {
              timeout: 8000,
              async: true,
              success: () => {
                if (this.loggedIdentifies.indexOf(szDeviceIdentify) < 0) {
                  this.loggedIdentifies.push(szDeviceIdentify);
                }
                setTimeout(onStart, 400);
              },
              error: (e) => {
                if (e && e.errorCode === 2001) {
                  if (this.loggedIdentifies.indexOf(szDeviceIdentify) < 0) {
                    this.loggedIdentifies.push(szDeviceIdentify);
                  }
                  setTimeout(onStart, 400);
                } else {
                  resolve();
                }
              },
            });
          });
          await new Promise((r) => setTimeout(r, 200));
        }
      };
      run();
    },

    teardownAll() {
      this.stopStreamsOnly();
      const WebVideoCtrl = this.getWv();
      if (WebVideoCtrl && typeof WebVideoCtrl.I_DestroyPlugin === 'function') {
        try {
          WebVideoCtrl.I_DestroyPlugin().then(
            () => {},
            () => {}
          );
        } catch (e) {
          /* ignore */
        }
      }
      this.pluginReady = false;
    },
  },
};
</script>

<style scoped lang="scss">
.hik-multi-wrap {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 360px;
  background: #000;
}
.hik-tip {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  z-index: 2;
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.45;
  color: #fde68a;
  background: rgba(30, 27, 15, 0.88);
  border-radius: 4px;
}
.hik-plugin-host {
  width: 100%;
  height: 100%;
  min-height: 360px;
}
</style>
